import { useState } from 'react';
import type { FormEvent } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import {
  useConvertEnquiry,
  useCreateEnquiry,
  useEnquiries,
} from '@/hooks/useDesk';
import { formatDate } from '@/lib/format';

export function EnquiriesPage() {
  const { data, isLoading, isError, error } = useEnquiries();
  const create = useCreateEnquiry();
  const convert = useConvertEnquiry();
  const [open, setOpen] = useState(false);
  const [contactName, setContactName] = useState('');
  const [eventName, setEventName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [guestCount, setGuestCount] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    await create.mutateAsync({
      contact_name: contactName,
      event_name: eventName || null,
      contact_email: email || null,
      contact_phone: phone || null,
      guest_count: guestCount ? Number(guestCount) : null,
      source: 'staff',
    });
    setContactName('');
    setEventName('');
    setEmail('');
    setPhone('');
    setGuestCount('');
    setOpen(false);
  }

  return (
    <div>
      <PageHeader
        title="Enquiries"
        description="Venue enquiries awaiting review or conversion."
        actions={
          <button
            type="button"
            className="rounded-lg bg-dome-forest px-3 py-2 text-sm font-medium text-dome-sand"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Close' : 'New enquiry'}
          </button>
        }
      />

      {open ? (
        <form
          onSubmit={onSubmit}
          className="mb-5 grid gap-3 rounded-xl border border-[var(--border)] bg-white/80 p-4 sm:grid-cols-2"
        >
          <label className="text-sm">
            <span className="mb-1 block font-medium">Contact name</span>
            <input
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              required
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Event name</span>
            <input
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Email</span>
            <input
              type="email"
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Phone</span>
            <input
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Guests</span>
            <input
              type="number"
              min={1}
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={guestCount}
              onChange={(e) => setGuestCount(e.target.value)}
            />
          </label>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-lg bg-dome-brass px-4 py-2 text-sm font-medium text-dome-ink disabled:opacity-60"
            >
              {create.isPending ? 'Saving…' : 'Save enquiry'}
            </button>
          </div>
          {create.isError ? (
            <p className="sm:col-span-2 text-sm text-dome-danger">
              {(create.error as Error).message}
            </p>
          ) : null}
        </form>
      ) : null}

      {isLoading ? (
        <p className="animate-pulse-soft text-sm text-[var(--muted)]">Loading…</p>
      ) : null}
      {isError ? (
        <p className="text-sm text-dome-danger">{(error as Error).message}</p>
      ) : null}

      {!isLoading && data && data.length === 0 ? (
        <EmptyState
          title="No enquiries yet"
          body="Create an enquiry to start the desk workflow, then convert it to an event."
        />
      ) : null}

      {data && data.length > 0 ? (
        <DataTable
          headers={['Contact', 'Event', 'Guests', 'Status', 'Created', '']}
        >
          {data.map((row) => (
            <tr key={row.id} className="align-middle">
              <td className="px-3 py-2">
                <div className="font-medium">{row.contact_name}</div>
                <div className="text-xs text-[var(--muted)]">
                  {row.contact_email || row.contact_phone || '—'}
                </div>
              </td>
              <td className="px-3 py-2">{row.event_name || '—'}</td>
              <td className="px-3 py-2">{row.guest_count ?? '—'}</td>
              <td className="px-3 py-2">
                <StatusBadge status={row.status} />
              </td>
              <td className="px-3 py-2 text-[var(--muted)]">
                {formatDate(row.created_at)}
              </td>
              <td className="px-3 py-2 text-right">
                {row.status !== 'converted' ? (
                  <button
                    type="button"
                    className="text-sm font-medium text-dome-leaf disabled:opacity-50"
                    disabled={convert.isPending}
                    onClick={() => convert.mutate(row.id)}
                  >
                    Convert
                  </button>
                ) : (
                  <span className="text-xs text-[var(--muted)]">Event linked</span>
                )}
              </td>
            </tr>
          ))}
        </DataTable>
      ) : null}
    </div>
  );
}
